import React, { ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import { Button, Dropdown, Form, OverlayTrigger, Tooltip } from "react-bootstrap"
import DateInput from "../../molecules/DateInput/date-input";
import { useValidation } from "../../../hooks/useValidation";
import { BsInfoCircle } from "react-icons/bs";
import { SelectOption } from "../form-view";
import { FormItemType } from "../component-map";
import { Controller } from "react-hook-form";
import { isAsync } from "../../../../services/table-helpers";
import { useSelector } from "react-redux";
import { getError } from "../../../../services/form-helpers";
import { RequiredLabel } from "./RequiredLabel";

const SearchableSelect = ({ disabled, invalid, onSelect, options, value, minWidth, title, onOpen = undefined as (() => void) | undefined }) => {
  const [search, setSearch] = useState("");
  const [show, setShow] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const selected = options.find((option) => String(option.id) === String(value ?? ''));
  const filtered = options.filter((option) =>
    `${option.text ?? ''} ${option.id ?? ''}`.toLowerCase().includes(search.trim().toLowerCase()));

  const select = (option) => {
    onSelect(option.id);
    setShow(false);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlight((current) => Math.min(current + 1, filtered.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlight((current) => Math.max(current - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (highlight >= 0 && highlight < filtered.length) select(filtered[highlight]);
    } else if (event.key === "Escape") {
      setShow(false);
    }
    event.stopPropagation();
  };

  return (
    <Dropdown
      show={show}
      onToggle={(next) => {
        setShow(next);
        if (next) {
          setSearch("");
          setHighlight(0);
          onOpen?.();
        }
      }}
    >
      <Dropdown.Toggle
        disabled={disabled}
        title={title}
        size="sm"
        variant="light"
        className={`searchable-select-toggle w-100 text-start d-flex align-items-center justify-content-between border${invalid ? ' border-danger' : ''}`}
        style={minWidth ? { minWidth } : undefined}
      >
        {/* Non-breaking space keeps the toggle at full height when nothing is selected. */}
        <span className="text-truncate">{selected?.text || '\u00A0'}</span>
      </Dropdown.Toggle>
      {/* Menu sizes to its widest option (capped) so long product names aren't clipped. */}
      <Dropdown.Menu style={{ width: 'max-content', minWidth: 260, maxWidth: 480, maxHeight: 300, overflowY: 'auto' }}>
        <div className="px-2 pb-1" style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
          <Form.Control
            autoFocus
            size="sm"
            placeholder="Search..."
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setHighlight(0);
            }}
            onKeyDown={onKeyDown}
          />
        </div>
        {filtered.map((option, index) => (
          <Dropdown.Item
            key={`${option.id}-${index}`}
            active={index === highlight}
            onMouseEnter={() => setHighlight(index)}
            onClick={() => select(option)}
            ref={index === highlight ? (element: HTMLElement | null) => element?.scrollIntoView?.({ block: 'nearest' }) : undefined}
          >
            {option.text}
          </Dropdown.Item>
        ))}
        {filtered.length === 0 && <div className="px-2 py-1 text-muted small">No matches</div>}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export const SelectField = ({ watch, field, control, fieldErrors, validation, getValues, data, rowIdentity, isUnique, required, register, errors, formState, editTableProps }) => {
  const isOptionsAsync = typeof field.options === 'function' && isAsync(field.options);
  const useSelectorOptions = typeof field.options === 'function' && !isAsync(field.options);
  const optionSelector = useSelector(useSelectorOptions ? field.options : () => null);
  const [result, setResult] = useState([] as any[]);
  const reloadDependencyValues = field.reloadOnFields?.length ? watch(field.reloadOnFields) : undefined;
  const reloadDependencyKey = JSON.stringify(reloadDependencyValues ?? []);
  // field.reloadOnOpen: re-run async options each time the searchable menu opens
  // (picks up records created elsewhere, e.g. in another tab, without a page refresh).
  const [openReloadTick, setOpenReloadTick] = useState(0);
  const [customValidation, customValidatorNames] = useValidation({ field, validation, getValues, data, rowIdentity, isUnique, errors });
  const hide = field.hideIf ? field.hideIf({ editTableProps, rowIdentity, watch, getValues }) : false;
  useEffect(() => {
    let active = true
    load()
    return () => { active = false }

    async function load() {
      if (isOptionsAsync) {
        const res = await (field.options as ((data: any, getValues?: any, editTableProps?: any) => Promise<any[]>))(data, getValues, editTableProps);
        if (!active) { return }
        setResult(res)
      }
    }
  }, [data, field.options, reloadDependencyKey, openReloadTick]);
  const renderTooltip = (props) => {
    return (
      <Tooltip id="button-tooltip" {...props} >
        {field.tooltip}
      </Tooltip>
    )
  }
  const options = isOptionsAsync
    ? result
    : (useSelectorOptions ? optionSelector : field.options) as SelectOption[];

  return hide ? null : (
    <Controller
      rules={{
        required: required && 'Required',
        validate: field.zeroIsEmpty
          ? { zeroIsEmpty: (value) => !required || String(value) !== '0' || 'Required' }
          : undefined,
      }}
      control={control}
      name={field.name}
      defaultValue={null}
      render={({
        field: {
          onChange, value,
        },
      }) => {
        const isDisabled = field.disabled ? field.disabled(watch, rowIdentity) : false;
        // A disabled searchable select renders a Bootstrap .btn (pointer-events:
        // none), which swallows the native title — so the hint lives on the
        // Form.Group div, which still shows the tooltip on hover.
        const disabledTitle = isDisabled ? field.disabledTooltip : undefined;
        return (
          <Form.Group className={editTableProps ? undefined : "mb-1"} title={disabledTitle ?? field.tooltip} style={field.style}>
            {field.label && <RequiredLabel label={field.label} required={required} />}
            {field.label && field.tooltip && (
              <><RequiredLabel label={field.label} required={required} />
                <OverlayTrigger
                  placement="top"
                  delay={{ show: 0, hide: 0 }}
                  overlay={renderTooltip}
                >
                  <Button variant="link" style={{ backgroundColor: 'transparent', color: 'unset', padding: 0, paddingLeft: 6 }}><BsInfoCircle style={{ marginTop: -6 }} size={15} /></Button>
                </OverlayTrigger></>
            )
            }
            {field.searchable ? (
              <SearchableSelect
                disabled={isDisabled}
                title={disabledTitle}
                invalid={!!fieldErrors}
                value={value}
                minWidth={field.minWidth}
                options={options ?? []}
                onOpen={field.reloadOnOpen && isOptionsAsync ? () => setOpenReloadTick((tick) => tick + 1) : undefined}
                onSelect={(selectedValue) => {
                  onChange(selectedValue)
                  const e = { target: { name: field.name, value: selectedValue } };
                  if (editTableProps?.refreshRow) {
                    editTableProps.refreshRow();
                  }
                  if (field.onChange) {
                    field.onChange({ getValues, editTableProps, evt: e, setValue: formState.setValue }, {})
                  }
                }}
              />
            ) : (
              <Form.Select
                disabled={isDisabled}
                title={disabledTitle}
                value={value ?? ''}
                style={field.minWidth ? { minWidth: field.minWidth } : undefined}
                onChange={(e) => {
                  onChange(e)
                  if (editTableProps?.refreshRow) {
                    editTableProps.refreshRow();
                  }
                  if (field.onChange) {
                    field.onChange({ getValues, editTableProps, evt: e, setValue: formState.setValue }, {})
                  }
                }} size="sm"
                isInvalid={fieldErrors}>
                {(options ?? []).map((option, index) => (
                  <option value={option.id} key={`option-${index}`}>{option.text}</option>
                ))}
              </Form.Select>
            )}
            {
              fieldErrors && <Form.Control.Feedback type="invalid">
                {'this field is required'}
              </Form.Control.Feedback>
            }
          </Form.Group>
        );
      }}
    />

  );
}